/**
 * Push notification helpers (§9.4 — web push + marketing topic subscriptions).
 *
 * The client retrieves an FCM token via the Firebase client SDK (`getToken`,
 * unavoidable — it's client-side) then registers it with the BFF so the BFF
 * can send booking-reminder pushes. Marketing topic subscriptions are also
 * BFF-mediated (guardrail 1 — no direct client Firebase calls beyond auth +
 * token retrieval).
 *
 * iOS Safari note: web push requires the site to be installed as a PWA
 * (Add to Home Screen) from iOS 16.4+. On non-installed iOS, `isPushSupported()`
 * returns false and callers fall back to email reminders. British English.
 */
import { getToken } from 'firebase/messaging';
import { getPushMessaging } from './firebase';
import { api } from './api';

/**
 * The VAPID public key for Firebase web push. Hard-coded (not env-var-driven)
 * because it's a PUBLIC key — it ships to every browser by design and has no
 * private counterpart on the client. This matches the existing pattern in
 * `firebase.ts`, where the full Firebase web config (apiKey, appId, etc.) is
 * also hard-coded as public-by-design values (see copilot-instructions.md +
 * MASTER-PROMPT.md §14). Hard-coding avoids the build-pipeline complexity of
 * passing a Vite env var through Docker build args / Cloud Build substitution
 * variables across staging + prod triggers.
 *
 * Generate in Firebase Console: ⚙ Project settings → Cloud Messaging →
 * Web Push certificates → Generate Key Pair. If the keypair is ever rotated,
 * update this constant + redeploy.
 */
const VAPID_KEY = 'BEo8_YPrmRIguabSR5Vqjwby8XBe4646AGw_CtUwdpnmcf-VNd33BC1zcC9jEifnz2mynD973PEDRk4Utc1Vx6U';

/** Whether this browser/device supports web push. */
export function isPushSupported(): boolean {
  if (!VAPID_KEY) return false;
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return false;
  if (typeof Notification === 'undefined') return false;
  // iOS Safari only supports web push from a PWA install (standalone mode).
  if (isIOSWithoutInstall()) return false;
  return true;
}

/** iOS-only hint — true when the user is on iOS without a PWA install. */
export function isIOSWithoutInstall(): boolean {
  if (typeof navigator === 'undefined') return false;
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
  if (!isIOS) return false;
  const standaloneNav = (navigator as unknown as { standalone?: boolean }).standalone === true;
  // matchMedia may be absent (jsdom); default to non-standalone when unavailable.
  const standaloneMql =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(display-mode: standalone)').matches
      : false;
  return !standaloneNav && !standaloneMql;
}

/** The outcome of a push-permission request, so callers can message accurately. */
export type PushRequestResult =
  | { status: 'granted'; token: string }
  | { status: 'denied' }
  | { status: 'unsupported' }
  | { status: 'error' };

/**
 * Get the existing FCM token WITHOUT prompting the user. Only returns a token
 * when notification permission is ALREADY granted. Used on page mount so the
 * app never triggers a non-gesture permission prompt (which Chrome/Edge
 * suppress and, after repeated dismissals, embargo at browser level).
 */
export async function getExistingPushToken(): Promise<string | null> {
  if (!isPushSupported()) return null;
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return null;
  try {
    const messaging = await getPushMessaging();
    if (!messaging) return null;
    return await getToken(messaging, { vapidKey: VAPID_KEY! });
  } catch {
    return null;
  }
}

/**
 * Request notification permission + get an FCM token, then register it with the
 * BFF. MUST be called from a user gesture (e.g. a toggle onClick) — browsers
 * suppress/embargo non-gesture prompts. Returns a structured outcome so the UI
 * can toast accurately (e.g. "blocked in browser settings" when denied).
 */
export async function requestPushPermissionAndToken(): Promise<PushRequestResult> {
  if (!isPushSupported()) return { status: 'unsupported' };

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return { status: 'denied' };

    const messaging = await getPushMessaging();
    if (!messaging) return { status: 'error' };

    const token = await getToken(messaging, { vapidKey: VAPID_KEY! });
    if (!token) return { status: 'error' };

    // Register with the BFF (idempotent). Platform = 'web' for browser
    // tokens; iOS PWAs still report 'web' (FCM doesn't distinguish).
    try {
      await api.post('/api/v1/me/fcm-token', { token, platform: 'web' });
    } catch {
      // Non-fatal — the token is still valid; we'll re-register next time.
    }
    return { status: 'granted', token };
  } catch {
    return { status: 'error' };
  }
}

/** Remove the given FCM token from the BFF (user toggled Notifications off). */
export async function unregisterPushToken(token: string): Promise<void> {
  try {
    // DELETE with a body — fetch directly (the api.delete helper has no body arg).
    await fetch('/api/v1/me/fcm-token', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, platform: 'web' }),
    });
  } catch {
    // Non-fatal.
  }
}

/** Subscribe a device token to the `marketing` FCM topic (opt-in). */
export async function subscribeToMarketingTopic(token: string): Promise<boolean> {
  try {
    const res = await api.post<{ ok: boolean; failureCount: number }>(
      '/api/v1/me/fcm-topic',
      { token, topic: 'marketing' },
    );
    return res.ok;
  } catch {
    return false;
  }
}

/** Unsubscribe a device token from the `marketing` FCM topic (opt-out). */
export async function unsubscribeFromMarketingTopic(token: string): Promise<boolean> {
  try {
    // DELETE with a body — fetch directly (the api.delete helper has no body arg).
    const res = await fetch('/api/v1/me/fcm-topic', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, topic: 'marketing' }),
    });
    return res.ok;
  } catch {
    return false;
  }
}